import { FlaskConical, History, Minus, Pencil, PlugZap, Plus, RotateCcw, ScrollText, Settings2, Snowflake, Undo2 } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { PolicyChangeKind, PolicyDiff, PolicyDiffEntry, PolicySimulationResult, PolicyVersionInfo } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, EmptyState, ErrorState, Panel, Pressable, RelativeTime, Skeleton, cn, toUiError, toast } from '@orgus/ui';
import type { Tone, UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { summarizeSimulation } from '../approvals/policyModel.ts';
import { fetchDiff, fetchVersions, rollback, simulateVersion, usePolicyV2 } from './live.ts';
import { CHANGE_LABEL, describeDiff } from './model.ts';
import { useProjects } from './LaunchSettingsView.tsx';

const KIND_ICON: Record<PolicyChangeKind, ReactNode> = {
  create: <Plus />,
  update: <Pencil />,
  delete: <Minus />,
  settings: <Settings2 />,
  freeze: <Snowflake />,
  rollback: <Undo2 />,
};

const CHANGE_META: Record<PolicyDiffEntry['change'], { tone: Tone; icon: ReactNode }> = {
  added: { tone: 'passed', icon: <Plus /> },
  removed: { tone: 'failed', icon: <Minus /> },
  changed: { tone: 'waiting', icon: <Pencil /> },
};

function DiffList({ diff, empty }: { diff: PolicyDiff; empty: string }) {
  const { name } = useProjects();
  if (!diff.entries.length) return <p className="px-4 py-3 text-sm text-fg-muted">{empty}</p>;
  return (
    <ul className="flex flex-col divide-y divide-border-subtle" aria-label={`Changes from version ${diff.from} to ${diff.to}`}>
      {diff.entries.map((e) => {
        const d = describeDiff(e, name);
        return (
          <li key={`${e.area}:${e.id}`} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
            <span className="w-[100px] shrink-0">
              <Badge tone={CHANGE_META[e.change].tone} icon={CHANGE_META[e.change].icon}>
                {CHANGE_LABEL[e.change]}
              </Badge>
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className={cn('text-fg [overflow-wrap:anywhere]', e.area === 'rule' && 'font-mono')}>{d.title}</span>
              {d.detail && <span className="text-fg-muted [overflow-wrap:anywhere]">{d.detail}</span>}
            </div>
            <span className="shrink-0 text-fg-subtle">{d.where}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Settings, Policy versions: every change with its author, what changed, and rollback. */
export function VersionsView() {
  const { phase, policy } = usePolicyV2();
  const loadError = useOrgus((s) => s.loadError);
  const [versions, setVersions] = useState<PolicyVersionInfo[] | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [diff, setDiff] = useState<PolicyDiff | UiError | null>(null);
  const [restoring, setRestoring] = useState<PolicyVersionInfo | null>(null);
  const [restoreDiff, setRestoreDiff] = useState<PolicyDiff | null>(null);
  const [restoreSim, setRestoreSim] = useState<PolicySimulationResult | UiError | null>(null);
  const current = policy?.version ?? 0;

  const load = async () => {
    setListError(null);
    try {
      setVersions(await fetchVersions());
    } catch (e) {
      setListError(e);
    }
  };
  useEffect(() => {
    if (phase === 'ready') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, current]);
  useEffect(() => {
    if (selected == null && versions?.length) setSelected(versions[0]!.version);
  }, [versions, selected]);
  useEffect(() => {
    if (selected == null) return;
    setDiff(null);
    fetchDiff(Math.max(0, selected - 1), selected).then(setDiff, (e) => setDiff(toUiError(e)));
  }, [selected, current]);
  useEffect(() => {
    if (!restoring) return;
    setRestoreDiff(null);
    setRestoreSim(null);
    fetchDiff(current, restoring.version).then(setRestoreDiff, () => setRestoreDiff({ from: current, to: restoring.version, entries: [] }));
    simulateVersion(restoring.version).then(setRestoreSim, (e) => setRestoreSim(toUiError(e)));
  }, [restoring, current]);

  if (phase === 'loading') return <Skeleton className="h-[420px] max-w-[960px]" />;
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !policy) return <EmptyState icon={<PlugZap />} title="Policy versions are not available on this hub" description="Update Orgus on the hub machine, then restart it." />;

  const sel = versions?.find((v) => v.version === selected) ?? null;
  return (
    <div className="grid max-w-[1100px] gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <Panel title="Versions" sub={`current is ${current}`} icon={<History />} flush>
        {listError ? (
          <div className="p-4">
            <ErrorState error={listError} variant="inline" onRetry={load} />
          </div>
        ) : !versions ? (
          <div className="flex flex-col gap-2 p-4" role="status" aria-label="Loading versions">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : versions.length === 0 ? (
          <EmptyState icon={<History />} title="No versions yet" description="Rule, settings and freeze changes show up here with who made them." />
        ) : (
          <ul className="flex max-h-[560px] flex-col divide-y divide-border-subtle overflow-auto" aria-label="Policy versions, newest first">
            {versions.map((v) => (
              <li key={v.version}>
                <Pressable
                  onClick={() => setSelected(v.version)}
                  aria-current={v.version === selected || undefined}
                  className={cn('flex items-start gap-3 px-4 py-2.5 hover:bg-hover', v.version === selected && 'bg-hover')}
                >
                  <span className="mt-0.5 text-fg-subtle [&_svg]:size-3.5" aria-hidden>
                    {KIND_ICON[v.kind]}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm text-fg">{v.summary}</span>
                    <span className="text-sm text-fg-subtle">
                      v{v.version} by {v.by}, <RelativeTime ts={v.at} />
                    </span>
                  </span>
                  {v.version === current && (
                    <Badge tone="accent" variant="outline">
                      Current
                    </Badge>
                  )}
                </Pressable>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title={sel ? `Version ${sel.version}` : 'Changes'}
        sub={sel ? `${sel.by}, ${new Date(sel.at).toLocaleString()}` : undefined}
        icon={<ScrollText />}
        flush
        actions={
          sel && sel.version !== current ? (
            <Button size="sm" variant="secondary" icon={<RotateCcw />} onClick={() => setRestoring(sel)}>
              Restore this version
            </Button>
          ) : undefined
        }
      >
        {!sel ? (
          <p className="px-4 py-3 text-sm text-fg-muted">Pick a version to see what it changed.</p>
        ) : diff == null ? (
          <div className="flex flex-col gap-2 p-4" role="status" aria-label="Loading changes">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : 'what' in diff ? (
          <div className="p-4">
            <ErrorState error={diff} variant="inline" />
          </div>
        ) : (
          <>
            <p className="border-b border-border-subtle px-4 py-2 text-sm text-fg-muted">What version {sel.version} changed from version {Math.max(0, sel.version - 1)}.</p>
            <DiffList diff={diff} empty="This version changed nothing that applies (for example, a setting saved with the same value)." />
          </>
        )}
      </Panel>

      <ConfirmDialog
        open={!!restoring}
        onOpenChange={(o) => !o && setRestoring(null)}
        title={restoring ? `Restore version ${restoring.version}?` : 'Restore version?'}
        description={restoring ? `The rules, launch settings, landing rules and freeze windows go back to how they were in version ${restoring.version}. This becomes version ${current + 1}, by you; nothing is deleted from the history.` : ''}
        confirmLabel="Restore version"
        tone="primary"
        onConfirm={async () => {
          if (!restoring) return;
          const r = await rollback(restoring.version);
          toast(`Restored version ${restoring.version}`, { tone: 'success', description: `Saved as version ${r.version}. The next request and the next launch use it.` });
          setSelected(r.version);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="rounded-md border border-border-subtle">
            {restoreDiff ? (
              <DiffList diff={restoreDiff} empty="Nothing would change: the current policy already matches." />
            ) : (
              <p className="px-4 py-3 text-sm text-fg-muted">Comparing with the current policy...</p>
            )}
          </div>
          {restoreSim == null ? (
            <p className="flex items-center gap-2 text-sm text-fg-muted">
              <FlaskConical className="size-3.5" aria-hidden /> Simulating against last week...
            </p>
          ) : 'what' in restoreSim ? (
            <p className="text-sm text-fg-muted">Could not simulate: {restoreSim.what}</p>
          ) : (
            <p className="flex items-start gap-2 text-sm text-fg">
              <FlaskConical className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" aria-hidden />
              {summarizeSimulation(restoreSim)}
            </p>
          )}
        </div>
      </ConfirmDialog>
    </div>
  );
}
